import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import classNames from 'classnames/bind';
import {
    LeftOutlined,
    RightOutlined,
    CalendarOutlined,
    UserOutlined,
    TeamOutlined,
    ClockCircleOutlined,
    GithubOutlined,
    GlobalOutlined,
    TagOutlined,
    YoutubeOutlined,
    ProjectOutlined,
} from '@ant-design/icons';

import { projects, projectDetails } from '../../data/projects';
import ProjectDetail from '../../components/Projects/ProjectDetail';
import styles from './ProjectDetails.module.scss';

const cx = classNames.bind(styles);

function ProjectDetails() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const [project, setProject] = useState(null);
    const [detail, setDetail] = useState(null);
    const [isVisible, setIsVisible] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        // Hàm tìm kiếm linh hoạt - hỗ trợ cả ID chính xác và ID có dấu gạch nối
        const findProjectFlexible = (searchId) => {
            // Tìm chính xác trước
            let foundIndex = projects.findIndex((p) => p.id === searchId);

            // Nếu không tìm thấy, thử loại bỏ dấu gạch nối cuối
            if (foundIndex === -1 && searchId.endsWith('-')) {
                const cleanId = searchId.slice(0, -1);
                foundIndex = projects.findIndex((p) => p.id === cleanId);
            }

            // Nếu vẫn không tìm thấy, thử thêm dấu gạch nối cuối
            if (foundIndex === -1 && !searchId.endsWith('-')) {
                const idWithDash = searchId + '-';
                foundIndex = projects.findIndex((p) => p.id === idWithDash);
            }

            return foundIndex;
        };

        // Tìm dự án theo ID linh hoạt
        const foundIndex = findProjectFlexible(projectId);

        if (foundIndex !== -1) {
            const actualProject = projects[foundIndex];
            setProject(actualProject);
            setCurrentIndex(foundIndex);

            // Nếu URL không khớp chính xác với ID thực, redirect đến URL đúng
            if (projectId !== actualProject.id) {
                navigate(`/projects/${actualProject.id}`, { replace: true });
                return;
            }

            // Kiểm tra xem có dữ liệu chi tiết cho dự án này không
            if (projectDetails[actualProject.id]) {
                setDetail(projectDetails[actualProject.id]);
            } else {
                // Nếu không có chi tiết, sử dụng dữ liệu cơ bản từ mảng projects
                setDetail({
                    title: actualProject.title,
                    description: actualProject.shortDescription,
                    features: [],
                    technologies: {
                        frontend: [],
                        backend: [],
                        database: [],
                        other: [],
                    },
                    screenshots: [],
                    previewLink: actualProject.previewLink,
                    githubLink: actualProject.githubLink,
                });
            }

            // Animation khi load trang
            setTimeout(() => {
                setIsVisible(true);
            }, 100);

            // Cuộn lên đầu trang
            window.scrollTo(0, 0);
        } else {
            // Dự án không tồn tại, chuyển hướng về trang dự án
            navigate('/projects');
        }
    }, [projectId, navigate]);

    // Xử lý chuyển đến dự án trước đó
    const handlePrevProject = () => {
        const prevIndex = (currentIndex - 1 + projects.length) % projects.length;
        navigate(`/projects/${projects[prevIndex].id}`);
    };

    // Xử lý chuyển đến dự án tiếp theo
    const handleNextProject = () => {
        const nextIndex = (currentIndex + 1) % projects.length;
        navigate(`/projects/${projects[nextIndex].id}`);
    };

    // Nếu chưa có dữ liệu, hiển thị loading
    if (!project || !detail) {
        return (
            <div className={cx('loading-container')}>
                <div className={cx('loading-spinner')}></div>
                <p>Đang tải thông tin dự án...</p>
            </div>
        );
    }

    return (
        <div className={cx('project-details-container')}>
            {/* Header của trang chi tiết */}
            <div className={cx('project-details-header', { visible: isVisible })}>
                <div className={cx('header-content')}>
                    <h1 className={cx('project-title')}>{detail.title}</h1>

                    <div className={cx('project-meta')}>
                        {project.completedDate && (
                            <div className={cx('meta-item')}>
                                <CalendarOutlined className={cx('meta-icon')} />
                                <span>Hoàn thành: {project.completedDate}</span>
                            </div>
                        )}

                        {project.role && (
                            <div className={cx('meta-item')}>
                                <UserOutlined className={cx('meta-icon')} />
                                <span>Vai trò: {project.role}</span>
                            </div>
                        )}

                        {/* Thêm hiển thị thông tin client */}
                        {project.client && (
                            <div className={cx('meta-item')}>
                                <ProjectOutlined className={cx('meta-icon')} />
                                <span>Phạm vi: {project.client}</span>
                            </div>
                        )}

                        {detail.teamSize && (
                            <div className={cx('meta-item')}>
                                <TeamOutlined className={cx('meta-icon')} />
                                <span>Kích thước nhóm: {detail.teamSize}</span>
                            </div>
                        )}

                        {detail.duration && (
                            <div className={cx('meta-item')}>
                                <ClockCircleOutlined className={cx('meta-icon')} />
                                <span>Thời gian: {detail.duration}</span>
                            </div>
                        )}
                    </div>

                    <div className={cx('project-links')}>
                        {project.githubLink && (
                            <a
                                href={project.githubLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={cx('project-link', 'github')}
                            >
                                <GithubOutlined />
                                <span>GitHub</span>
                            </a>
                        )}
                        {project.youtubeLink && (
                            <a
                                href={project.youtubeLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={cx('project-link', 'youtube')}
                            >
                                <YoutubeOutlined />
                                <span>Demo Video</span>
                            </a>
                        )}
                        {project.previewLink && (
                            <a
                                href={project.previewLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={cx('project-link', 'preview')}
                            >
                                <GlobalOutlined />
                                <span>Live Demo</span>
                            </a>
                        )}
                    </div>

                    <div className={cx('project-tags')}>
                        <TagOutlined className={cx('tags-icon')} />
                        {project.tags.map((tag, index) => (
                            <span key={index} className={cx('tag')}>
                                {tag}
                            </span>
                        ))}
                    </div>
                </div>

                {/* Hình thumbnail */}
                <div className={cx('header-image')}>
                    <img src={project.thumbnail || '/images/projects/placeholder.jpg'} alt={project.title} />
                </div>
            </div>

            {/* Phần chi tiết dự án */}
            <ProjectDetail detail={detail} isVisible={isVisible} />

            {/* Navigation giữa các dự án */}
            <div className={cx('project-navigation', { visible: isVisible })}>
                <button className={cx('nav-button', 'prev')} onClick={handlePrevProject}>
                    <LeftOutlined />
                    <span>Dự án trước</span>
                </button>

                <Link to="/projects" className={cx('nav-button', 'all')}>
                    <span>Tất cả dự án</span>
                </Link>

                <button className={cx('nav-button', 'next')} onClick={handleNextProject}>
                    <span>Dự án tiếp theo</span>
                    <RightOutlined />
                </button>
            </div>
        </div>
    );
}

export default ProjectDetails;
